import * as React from 'react'

import { F0Select } from '@factorialco/f0-react'
import { GetCurrenciesDocument } from 'generated/resources/graphql'

import { useCallback } from 'modules/core/hooks/useCallback'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

interface Props {
  value?: string
  onChange?: (value: string) => void
  readonly?: boolean
  disabled?: boolean
  label?: string
  hint?: string
  placeholder?: string
  size?: 'sm' | 'md' | 'lg'
  error?: string | boolean
  filteredCurrencies?: string[]
}

const CurrencySelector = ({
  readonly = false,
  disabled = false,
  label = i18n.t('expenses_v2.account_form.currency'),
  hint,
  placeholder,
  size = 'md',
  error,
  value,
  onChange,
  filteredCurrencies,
}: Props) => {
  const { data, loading } = useQuery(GetCurrenciesDocument)

  const options = React.useMemo(() => {
    const currencies = data?.apiCore.currenciesConnection.edges.map((edge) => edge.node) ?? []
    const filteredCurrenciesSet = new Set(filteredCurrencies)

    return currencies
      .filter(
        (currency) => !filteredCurrenciesSet.size || filteredCurrenciesSet?.has(currency.isoCode)
      )
      .map((currency) => {
        return {
          label: `${currency.symbol} ${currency.isoCode}`,
          value: currency.isoCode,
        }
      })
  }, [data, filteredCurrencies])

  const searchCurrencyOption = useCallback(
    (option: { label: string; value: string }, search: string): boolean => {
      const term = search?.trim().toLowerCase() || ''

      if (term.length === 0) {
        return true
      }

      const optionLabel = option.label?.toLowerCase() || ''
      const optionValue = option.value?.toLowerCase() || ''

      return optionLabel.includes(term) || optionValue.includes(term)
    },
    []
  )

  return (
    <F0Select
      label={label}
      hint={hint}
      placeholder={placeholder}
      options={options}
      value={value ?? ''}
      onChange={onChange}
      disabled={disabled || readonly}
      error={error}
      loading={loading}
      size={size}
      searchFn={searchCurrencyOption}
      showSearchBox
    />
  )
}

export default CurrencySelector
